import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import EventAPI from "../api/EventApi";
import { capture, EVENTS } from "../analytics";
import { useParams, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import UserAPI from "../api/UserApi";
import "react-datepicker/dist/react-datepicker.css";
import Accordion from "../components/Accordion";
import { FormSection } from "./FormSection";
import { QueueSection } from "./QueueSection";
import {
  EventStreamProvider,
  useEventStream,
} from "../context/EventStreamingContext";
import { Connections } from "./Matches";
import { Reports } from "./Reports";
import { Reviews } from "./Reviews";
import { Chats } from "./Chats";
import {
  ChevronLeftIcon,
  PencilSquareIcon,
  PlusIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  QueueListIcon,
  LinkIcon,
  DocumentChartBarIcon,
  StarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  TicketIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import Api from "../api/api";
import { showToast } from "../utils/showToast";
import { InviteSection } from "./InviteSection";
import { GlobalContext } from "../context/GlobalContext";
import AITextExtractor from "../components/AITextExtractor";
import { EmailSection } from "./EmailSection";
import Coupons from "../Coupons";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";
import { showsReviews } from "./utils/reviews";

const TYPE_LABELS = { event_rsvp: "Ticket", event_join: "Ticket", prioritization: "Boost" };

const SEVERITY_STYLES = {
  high: { banner: "bg-red-50 border-red-200 dark:bg-red-900/40 dark:border-red-800", badge: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", icon: ExclamationTriangleIcon, iconColor: "text-red-500 dark:text-red-400" },
  medium: { banner: "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/30 dark:border-yellow-800", badge: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300", icon: ExclamationTriangleIcon, iconColor: "text-yellow-500 dark:text-yellow-400" },
  low: { banner: "bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700", badge: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300", icon: InformationCircleIcon, iconColor: "text-gray-400 dark:text-gray-500" },
};

// An issue the latest run didn't re-raise is kept on the doc so a flaky search
// result can't make a flag blink out, but its numbers are frozen at the run that
// found it. Date it so it can't read as current under the banner's "checked Nm ago".
const staleLabel = (issue) => {
  if (!issue.stale) return null;
  if (!issue.staleSince) return "from an earlier check";
  const found = new Date(issue.staleSince);
  if (Number.isNaN(found.getTime())) return "from an earlier check";
  return `found ${found.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
};

const EventHealthBanner = ({ lastHealthCheck, onDismissIssue }) => {
  const [expanded, setExpanded] = useState(false);

  if (!lastHealthCheck) return null;

  const { hasIssues, issues = [], severity, checkedAt } = lastHealthCheck;

  const checkedAgo = (() => {
    if (!checkedAt) return null;
    const diff = Date.now() - new Date(checkedAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  })();

  const topStyle = hasIssues ? (SEVERITY_STYLES[severity] || SEVERITY_STYLES.low) : null;

  if (!hasIssues) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-2 dark:border-green-800 dark:bg-green-900/40">
        <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0 dark:text-green-400" />
        <span className="text-sm text-green-800 font-medium dark:text-green-300">All clear</span>
        {checkedAgo && <span className="text-xs text-green-600 ml-auto dark:text-green-400">checked {checkedAgo}</span>}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${topStyle.banner} overflow-hidden`}>
      {/* Header row */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full px-4 py-3 flex items-center gap-2 text-left"
      >
        <ShieldCheckIcon className={`h-4 w-4 flex-shrink-0 ${topStyle.iconColor}`} />
        <span className="text-sm font-medium text-gray-900 flex-1 dark:text-gray-100">
          {issues.length} health issue{issues.length !== 1 ? "s" : ""} found
        </span>
        {checkedAgo && <span className="text-xs text-gray-400 dark:text-gray-500">checked {checkedAgo}</span>}
        <svg
          className={`h-4 w-4 text-gray-400 transition-transform dark:text-gray-500 ${expanded ? "rotate-180" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Issue list */}
      {expanded && (
        <div className="border-t border-inherit divide-y divide-inherit">
          {issues.map((issue, i) => {
            const style = SEVERITY_STYLES[issue.severity] || SEVERITY_STYLES.low;
            const Icon = style.icon;
            const stale = staleLabel(issue);
            return (
              <div key={i} className={`px-4 py-3 space-y-1${stale ? " opacity-75" : ""}`}>
                <div className="flex items-start gap-2">
                  <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${style.iconColor}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{issue.title}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${style.badge}`}>{issue.severity}</span>
                      {stale && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium dark:bg-gray-800 dark:text-gray-300">{stale}</span>
                      )}
                      {issue.canReschedule && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium dark:bg-blue-900/40 dark:text-blue-300">can reschedule</span>
                      )}
                      {issue.suggestCancel && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium dark:bg-red-900/40 dark:text-red-300">consider cancelling</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5 dark:text-gray-300">{issue.description}</p>
                    <p className="text-xs text-gray-500 mt-1 italic dark:text-gray-400">💡 {issue.suggestion}</p>
                  </div>
                  {onDismissIssue && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDismissIssue(issue.type); }}
                      className="flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors dark:text-gray-500 dark:hover:text-gray-300"
                      title="Dismiss issue"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RevenueSection = ({ revenue: revenueProp, isSuperAdmin, eventUsers, eventID, vendorID, currentVendorID }) => {
  // GIS-181 Track B: revenue moved to the gated events/{id}/finance/summary doc.
  // Seed from the prop for an instant paint, then fetch the authoritative finance
  // value (the event doc no longer carries revenue after the scrub).
  const [revenue, setRevenue] = useState(
    revenueProp || { total: 0, tickets: 0, prioritizations: 0 }
  );
  const [payments, setPayments] = useState([]);
  const [refunds, setRefunds] = useState({});
  const [confirmingId, setConfirmingId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // The host of THIS event (vendorID match) or any super admin may see who paid
  // and issue refunds. Hosts only ever load their own events, but we still gate
  // explicitly so an admin viewing someone else's event is covered by isSuperAdmin.
  const canManage = isSuperAdmin || (!!vendorID && vendorID === currentVendorID);

  useEffect(() => {
    if (!canManage || !eventID) return;
    Promise.all([
      EventAPI.getRevenue(eventID),
      EventAPI.getProcessedPayments(eventID),
      EventAPI.getRefundedPayments(eventID),
    ])
      .then(([rev, pmts, rfnds]) => {
        if (rev) {
          setRevenue({
            total: Number(rev.total) || 0,
            tickets: Number(rev.tickets) || 0,
            prioritizations: Number(rev.prioritizations) || 0,
          });
        }
        setPayments(pmts);
        setRefunds(rfnds || {});
      })
      .catch(console.error);
  }, [eventID, canManage]);

  // null = refundable, "processing" = in flight, "refunded" = settled.
  const refundState = (paymentIntentId) => {
    const r = refunds[paymentIntentId];
    if (!r) return null;
    if ((r.amountRefundedCents || 0) > 0 || r.status === "refunded") return "refunded";
    if (r.status === "processing") return "processing";
    return null;
  };

  const handleRefund = async (p) => {
    setBusyId(p.paymentIntentId);
    try {
      await EventAPI.refundEventTicket(eventID, p.paymentIntentId);
      showToast("Refund issued — the attendee has been notified.", { type: "success" });
      // Reflect the in-flight refund immediately; the Stripe webhook finalizes it.
      setRefunds((prev) => ({
        ...prev,
        [p.paymentIntentId]: { ...(prev[p.paymentIntentId] || {}), status: "processing" },
      }));
    } catch (err) {
      showToast(err?.message || "Could not issue the refund.", { type: "error" });
    } finally {
      setBusyId(null);
      setConfirmingId(null);
    }
  };

  const rows = [
    { label: "Ticket Sales", value: revenue.tickets },
    { label: "Boosts", value: revenue.prioritizations },
  ].filter(r => r.value > 0);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between text-lg font-semibold text-gray-900 dark:text-gray-100">
        <span>Total Revenue</span>
        <span className="text-green-700 dark:text-green-300">${revenue.total.toFixed(2)}</span>
      </div>
      {rows.length > 0 && (
        <div className="border-t pt-3 space-y-2 dark:border-gray-700">
          {rows.map(r => (
            <div key={r.label} className="flex justify-between text-sm text-gray-600 dark:text-gray-300">
              <span>{r.label}</span>
              <span>${r.value.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
      {canManage && payments.length > 0 && (
        <div className="border-t pt-3 space-y-3 dark:border-gray-700">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide dark:text-gray-400">Paid by</p>
          {payments.map(p => {
            const user = eventUsers?.[p.userID];
            const state = refundState(p.paymentIntentId);
            const isBusy = busyId === p.paymentIntentId;
            const confirming = confirmingId === p.paymentIntentId;
            return (
              <div key={p.paymentIntentId} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {user?.photoURL ? (
                      <img src={variantURL(user.photoURL, 200)} onError={fallbackToOriginal(user.photoURL)} alt={user.name} className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                        {user?.name?.[0] ?? "?"}
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-800 dark:text-gray-200">{user?.name ?? p.userID ?? "Unknown"}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{TYPE_LABELS[p.type] ?? p.type} · {p.processedAt?.toDate().toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${state === "refunded" ? "text-gray-400 line-through dark:text-gray-500" : "text-green-700 dark:text-green-300"}`}>
                      ${p.amountDollars.toFixed(2)}
                    </span>
                    {state === "refunded" ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">Refunded</span>
                    ) : state === "processing" ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Refunding…</span>
                    ) : !confirming ? (
                      <button
                        type="button"
                        onClick={() => setConfirmingId(p.paymentIntentId)}
                        className="text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Refund
                      </button>
                    ) : null}
                  </div>
                </div>
                {confirming && state == null && (
                  <div className="flex items-center justify-between gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 dark:bg-red-900/30 dark:border-red-800">
                    <span className="text-xs text-red-700 dark:text-red-300">
                      Refund ${p.amountDollars.toFixed(2)} to {user?.name ?? "this attendee"}? They'll be notified.
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => setConfirmingId(null)}
                        className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => handleRefund(p)}
                        className="text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded px-2 py-1 disabled:opacity-50"
                      >
                        {isBusy ? "Refunding…" : "Confirm refund"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Host profile-photo gate: a host may only CREATE an event once their profile
// photo has passed review (users/{uid}.photoValidation.has_single_face === true).
// Super admins are exempt — they create on behalf of any vendor and needn't carry
// a host photo of their own. Editing is never gated. Seed from the cached user doc
// for an instant paint, then confirm against the live doc, since photoValidation is
// written asynchronously after a photo upload and the cache can lag.
const useHostPhotoGate = (isNewEvent) => {
  const { globalState } = useContext(GlobalContext);
  const gateApplies = isNewEvent && !globalState?.isSuperAdmin;
  const [hostPhotoValidated, setHostPhotoValidated] = useState(
    globalState?.user?.photoValidation?.has_single_face === true
  );

  const refreshHostPhotoStatus = useCallback(async () => {
    const uid = globalState?.user?.userID || globalState?.vendorID;
    if (!uid) return;
    try {
      const user = await UserAPI.getUserById(uid);
      setHostPhotoValidated(user?.photoValidation?.has_single_face === true);
    } catch (err) {
      console.error("Failed to check host photo validation:", err);
    }
  }, [globalState?.user?.userID, globalState?.vendorID]);

  useEffect(() => {
    if (gateApplies) refreshHostPhotoStatus();
  }, [gateApplies, refreshHostPhotoStatus]);

  return {
    photoGateBlocked: gateApplies && !hostPhotoValidated,
    refreshHostPhotoStatus,
  };
};

const HostPhotoGateBanner = ({ onRecheck }) => {
  const navigate = useNavigate();
  return (
    <div className="max-w-4xl mx-auto px-6">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/30">
        <div className="flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-500 dark:text-amber-400" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              Add a profile photo to create events
            </p>
            <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
              Your profile photo needs to pass our quick review before you can create
              an event. Add or update it in Settings, then re-check here.
            </p>
            <div className="mt-2 flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate("/settings")}
                className="text-xs font-semibold text-amber-800 underline hover:text-amber-900 dark:text-amber-200 dark:hover:text-amber-100"
              >
                Go to Settings
              </button>
              <button
                type="button"
                onClick={onRecheck}
                className="text-xs font-medium text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
              >
                Re-check
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Shown when the form was opened as a copy of another event, so a pre-filled
// form can't be mistaken for an edit of the event it was copied from.
const DuplicateSourceBanner = ({ sourceName }) => (
  <div className="max-w-4xl mx-auto px-6">
    <div className="rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 flex items-start gap-2">
      <DocumentDuplicateIcon className="h-4 w-4 text-primaryDark flex-shrink-0 mt-0.5 dark:text-primary" />
      <p className="text-sm text-gray-700 dark:text-gray-200">
        Copy of <span className="font-medium text-gray-900 dark:text-gray-100">{sourceName}</span>.
        Pick a date and save to create a new event. It starts free with no guests,
        so set the price before you publish.
      </p>
    </div>
  </div>
);

const EventFormContent = ({ eventID, embedded }) => {
  const { eventData, setEventData } = useEventStream();
  const { globalState, setGlobalState } = useContext(GlobalContext);
  const { photoGateBlocked, refreshHostPhotoStatus } = useHostPhotoGate(!eventID);
  const [openIndices, setOpenIndices] = useState([]);
  const { state: routerLocationState } = useLocation();
  const [searchParams] = useSearchParams();
  const [locationState] = useState(() => {
    const draftKey = searchParams.get("draftKey");
    if (draftKey) {
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          localStorage.removeItem(draftKey);
          return { ...(routerLocationState || {}), ...JSON.parse(raw) };
        }
      } catch (error) {
        console.error("Failed to read event draft:", error);
      }
    }
    return routerLocationState;
  });


  const toggleAccordion = (index) => {
    if (openIndices.includes(index)) {
      setOpenIndices(openIndices.filter((i) => i !== index));
    } else {
      setOpenIndices([...openIndices, index]);
    }
  };

  useEffect(() => {
    setGlobalState((prevState) => ({ ...prevState, currentEventObj: eventData }))
    return () => {
      setGlobalState((prevState) => ({ ...prevState, currentEventObj: null }))
    }
  }, [eventData, setGlobalState])

  useEffect(() => {
    return () => {
      setGlobalState((prevState) => ({ ...prevState, currentEventObj: null }))
    }
  }, [setGlobalState])

  // Seed the create form from the event being duplicated. useEventForm re-derives
  // its state from eventData, so writing the copied fields here fills the form
  // through the normal create path — nothing about saving is special-cased.
  //
  // This replaces rather than merges. The copy list is the whole truth about what
  // carries over, so anything already in state is by definition something the
  // duplicate decided to leave behind.
  useEffect(() => {
    if (!eventID && locationState?.duplicateFrom) {
      setEventData({ ...locationState.duplicateFrom });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Inject suggestion image into photos on new-event creation
  useEffect(() => {
    if (!eventID && locationState?.suggestionImageUrl) {
      setEventData((prev) => {
        const existing = prev?.photos || [];
        if (existing.includes(locationState.suggestionImageUrl)) return prev;
        return { ...(prev || {}), photos: [locationState.suggestionImageUrl, ...existing] };
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Automatically expand Create Event Form when eventData is populated via AI Text Extractor.
  // Ignore `photos` so the suggestion-image injection alone doesn't trip this.
  useEffect(() => {
    if (!eventID && eventData && Object.keys(eventData).some((k) => k !== "photos") && !openIndices.includes(0)) {
      setOpenIndices((prev) => [...prev, 0]);
    }
  }, [eventData, eventID, openIndices]);

  // The top of the create funnel (GIS-215): the host has put something real in
  // the form. This is what `admin_event_create_submitted` is measured against,
  // so what counts as "real" decides whether abandonment reads as 20% or 90%.
  //
  // Two things are deliberately NOT the trigger:
  //   - Opening /events/new. That is already a pageview, and treating a
  //     mis-click as a started draft turns every stray navigation into an
  //     abandonment.
  //   - `photos` alone. The suggestion-image injection and the duplicate-event
  //     prefill both write state before the host has typed anything, which would
  //     count a draft the host never started. Same exclusion, same reason, as
  //     the accordion effect above.
  //
  // Fires at most once per mount, via a ref rather than state — a re-render on
  // every keystroke is exactly what this sits in the path of.
  const draftStartedRef = useRef(false);
  useEffect(() => {
    if (eventID || draftStartedRef.current) return;
    const meaningful = eventData && Object.keys(eventData).some((k) => k !== "photos");
    if (!meaningful) return;
    draftStartedRef.current = true;
    capture(EVENTS.EVENT_DRAFT_STARTED, {
      // How the host got here, so abandonment can be read per entry point —
      // a prefilled duplicate and a blank form are not the same funnel.
      // `suggestionId` and `sourceRequestId` are the handoff keys the router
      // state actually uses — see the FormSection props below, which read the
      // same two. They are not named symmetrically and it is easy to invent a
      // third name here that is quietly always undefined.
      origin: locationState?.duplicateFrom
        ? "duplicate"
        : locationState?.suggestionId
        ? "suggestion"
        : locationState?.sourceRequestId
        ? "request"
        : "blank",
    });
  }, [eventData, eventID, locationState]);


  const handleDismissIssue = async (issueType) => {
    if (!window.confirm("Remove this issue from the health check?")) return;
    const current = eventData.lastHealthCheck;
    const filtered = (current.issues || []).filter((i) => i.type !== issueType);
    const severity = filtered.length > 0
      ? (filtered.some((i) => i.severity === "high") ? "high" : filtered.some((i) => i.severity === "medium") ? "medium" : "low")
      : null;
    try {
      // Dotted paths touch only the fields a dismissal changes. Rewriting the
      // whole lastHealthCheck map from this held snapshot would clobber
      // sibling fields (checkedAt etc.) a concurrent health-agent run wrote.
      await Api.updateDocument("events", eventID, {
        "lastHealthCheck.issues": filtered,
        "lastHealthCheck.issueCount": filtered.length,
        "lastHealthCheck.hasIssues": filtered.length > 0,
        "lastHealthCheck.severity": severity,
      });
    } catch (error) {
      console.error("Error dismissing health issue:", error);
      showToast("Failed to dismiss the issue", { type: "error" });
    }
  };

  // Owner-or-admin, the gate for the money-and-guest-facing sections below.
  const canManageEvent =
    !!eventID &&
    !!eventData &&
    (globalState.isSuperAdmin || eventData.vendorID === globalState.vendorID);

  // Same shape, but on the signed claim rather than isSuperAdmin, which is the
  // claim OR the vendors.isSuperAdmin flag. The coupon callables recognise only
  // the claim, so a flag-only operator opening someone else's event would get a
  // section whose every call comes back permission-denied. Revenue above renders
  // data the client already holds, so it does not have this problem.
  const canManageCoupons =
    !!eventID &&
    !!eventData &&
    (globalState.hasSuperAdminClaim || eventData.vendorID === globalState.vendorID);

  const accordionItems = [
    {
      title: eventID ? "Edit Event" : "Create Event Form",
      icon: eventID ? <PencilSquareIcon className="h-6 w-6" /> : <PlusIcon className="h-6 w-6" />,
      content: <FormSection sourceSuggestionId={locationState?.suggestionId} sourceRequestId={locationState?.sourceRequestId} photoGateBlocked={photoGateBlocked} />,
    },
    // GIS-222: co-hosts are picked here too, from a guest's photo in the guest list.
    ...(eventID
      ? [
        {
          title: "Guests & Invites",
          icon: <UserGroupIcon className="h-6 w-6" />,
          content: <InviteSection eventID={eventID} eventData={eventData} />,
        },
      ]
      : []),
    ...(eventID
      ? [
        {
          title: "Reservations & Email",
          icon: <EnvelopeIcon className="h-6 w-6" />,
          content: <EmailSection eventID={eventID} eventData={eventData} />,
        },
      ]
      : []),
    ...(eventID
      ? [
        {
          title: "Chats",
          icon: <ChatBubbleLeftRightIcon className="h-6 w-6" />,
          content: <Chats eventData={eventData} eventID={eventID} />,
        },
      ]
      : []),
    // An event without mixer check-in has no queue or session to run, and the
    // server refuses to start one, so the section would only offer dead buttons.
    ...(eventID && eventData?.requireCheckIn !== false ? [{ id: "queue", title: "Queue", icon: <QueueListIcon className="h-6 w-6" />, content: <QueueSection /> }] : []),
    ...(canManageEvent
      ? [
        {
          title: "Revenue",
          icon: <CurrencyDollarIcon className="h-6 w-6" />,
          content: <RevenueSection revenue={eventData.revenue} isSuperAdmin={globalState.isSuperAdmin} eventUsers={eventData.eventUsers} eventID={eventID} vendorID={eventData.vendorID} currentVendorID={globalState.vendorID} />,
        },
      ]
      : []),
    ...(canManageCoupons
      ? [
        {
          title: "Coupons",
          icon: <TicketIcon className="h-6 w-6" />,
          content: <Coupons eventID={eventID} eventData={eventData} />,
        },
      ]
      : []),
    ...(eventID && eventData?.isCompleted && globalState.isSuperAdmin
      ? [
        {
          title: "Connections",
          icon: <LinkIcon className="h-6 w-6" />,
          content: <Connections eventID={eventID} eventData={eventData} />,
        },
      ]
      : []),
    ...(eventID && eventData?.isCompleted && globalState.isSuperAdmin
      ? [
        {
          title: "Reports",
          icon: <DocumentChartBarIcon className="h-6 w-6" />,
          content: <Reports eventID={eventID} />,
        },
      ]
      : []),
    // "How was it?" ratings, for the host, co-hosts and admins. Keep it LAST:
    // the accordion is keyed by index, and this appears when the event completes.
    ...(eventID && showsReviews(eventData, { uid: globalState.vendorID, isSuperAdmin: globalState.isSuperAdmin })
      ? [
        {
          title: "Reviews",
          icon: <StarIcon className="h-6 w-6" />,
          content: <Reviews eventID={eventID} eventData={eventData} isAdminOperator={!!globalState.isSuperAdmin} />,
        },
      ]
      : []),
  ];

  // Expand the Queue section when the QR goes up. The position is looked up
  // rather than hardcoded: the sections above Queue are conditional, so its index
  // moves as they appear (it was 1 when this was written, it is 4 now, and index
  // 1 has been "Guests & Invites" for a while).
  //
  // openIndices is deliberately not a dependency. With it, closing the section
  // changed the dep, re-ran this, and re-opened it, so the section could not be
  // shut at all while the QR was up. The functional update is what makes reading
  // the current value safe without the dep.
  const queueIndex = accordionItems.findIndex((item) => item.id === "queue");
  useEffect(() => {
    if (!globalState.isShowingQR || queueIndex < 0) return;
    setOpenIndices((prev) => (prev.includes(queueIndex) ? prev : [...prev, queueIndex]));
  }, [globalState.isShowingQR, queueIndex]);

  return (
    <div className="space-y-4">
      {!embedded && (
        <a href="/events" className="text-primary px-3 py-2 flex items-center">
          <ChevronLeftIcon className="h-5 w-5 inline-block" />
          Back to Events
        </a>
      )}
      {!embedded && (
        <h1 className="text-xl text-slate-600 text-center font-semibold my-6 max-w-4xl mx-auto text-nowrap text-ellipsis overflow-hidden dark:text-slate-300">
          {eventData?.name}
        </h1>
      )}
      {photoGateBlocked && (
        <HostPhotoGateBanner onRecheck={refreshHostPhotoStatus} />
      )}
      {!eventID && locationState?.duplicateFrom && (
        <DuplicateSourceBanner sourceName={locationState.duplicateFrom.name} />
      )}
      {/* Brainstorming is for a blank slate. A copy already has its content, and
          the extractor writes straight to eventData, so leaving it up would
          invite overwriting the fields that were just pre-filled. */}
      {!eventID && !locationState?.duplicateFrom && (
        <AITextExtractor
          onExtractedData={setEventData}
          initialMessage={locationState?.suggestionMessage ?? ''}
        />
      )}
      {eventID && eventData?.lastHealthCheck && (
        <div className="max-w-4xl mx-auto px-6">
          <EventHealthBanner lastHealthCheck={eventData.lastHealthCheck} onDismissIssue={handleDismissIssue} />
        </div>
      )}
      <Accordion
        items={accordionItems}
        openIndices={openIndices}
        onToggle={toggleAccordion}
      />
    </div>
  );
};

const EventForm = ({ eventID: eventIDProp, embedded }) => {
  const { eventID: eventIDParam } = useParams();
  const eventID = eventIDProp || eventIDParam;

  // Keyed so the stream starts clean whenever the event identity changes.
  // /events/:eventID/edit and /events/new both render <Home><EventForm /></Home>,
  // so React reuses this component across that navigation and the provider would
  // otherwise keep serving the event we just left. That is how Duplicate ended up
  // showing an Event URL on an unsaved copy: the loaded event was still in state,
  // its id survived underneath the copied fields, and handleSubmit branches on
  // event.id, so saving would have updated the original instead of creating a new
  // one.
  return (
    <EventStreamProvider key={eventID || "new"} eventID={eventID}>
      <EventFormContent eventID={eventID} embedded={embedded} />
    </EventStreamProvider>
  );
};

export default EventForm;
